import { useSearchParams } from "react-router-dom";

type MapToStringOrNull<T extends readonly string[], D extends Partial<Record<string, string>>> = {
  [K in keyof T]: T[K] extends keyof D ? string : string | null;
};

/**
 * 自定义 hook 用于获取 URL searchParams（查询参数）
 * @param keys - 单个参数名或参数名数组
 * @param defaultValue - 默认值（单个参数时为字符串，多个参数时为对象）
 * @returns 如果传入 string，返回对应的值；如果传入 string[]，返回对应顺序的数组
 *
 * @example
 * // 获取单个参数（无默认值）
 * const id = useQueryParams("id"); // string | null
 *
 * @example
 * // 获取单个参数（有默认值）
 * const id = useQueryParams("id", "default"); // string
 *
 * @example
 * // 获取多个参数（无默认值）
 * const [id, type] = useQueryParams(["id", "type"]);
 * // [string | null, string | null]
 *
 * @example
 * // 获取多个参数（部分有默认值）
 * const [id, type, source] = useQueryParams(["id", "type", "source"], { type: "all" });
 * // [string | null, string, string | null]
 */

// 单个参数，无默认值
function useQueryParams<T extends string>(key: T): string | null;

// 单个参数，有默认值
function useQueryParams<T extends string>(key: T, defaultValue: string): string;

// 多个参数，无默认值
function useQueryParams<T extends readonly string[]>(keys: T): { [K in keyof T]: string | null };

// 多个参数，有默认值（部分或全部）
function useQueryParams<T extends readonly string[], D extends Partial<Record<T[number], string>>>(
  keys: T,
  defaultValues: D,
): MapToStringOrNull<T, D>;

// 实现
function useQueryParams<T extends string | readonly string[]>(
  keys: T,
  defaultValue?: string | Partial<Record<string, string>>,
): any {
  const [searchParams] = useSearchParams();

  if (typeof keys === "string") {
    const value = searchParams.get(keys);
    return value ?? (defaultValue as string | undefined) ?? null;
  }

  const defaults = (defaultValue as Partial<Record<string, string>>) || {};
  return keys.map((key) => {
    const value = searchParams.get(key);
    return value ?? defaults[key] ?? null;
  });
}

export { useQueryParams };
export default useQueryParams;
